/* UTILS */
import router from "@/router"
import { setup, createActor, fromPromise } from "xstate"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { type GlobalMachineEvent } from "@/machines/types"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"

/* API */
import { logout } from "@/api/viktor/authentication"

/* STORES */
import { useUserStore } from "@/stores/user"
import { useConfigStore } from "@/stores/config"
import { useAnswersStore } from "@/features/screening/store/answers.store"
import { storeToRefs } from "pinia"

const { getSessionItem } = useSessionStorage()

const getGotoParam = (): string | null => {
  const originalSearchParams = new URLSearchParams(window.location.search)

  const goto_param =
    originalSearchParams.get("goto") || getSessionItem("goto_param")

  return goto_param
}

const removeGoToSessionItem = () => {
  const { isFromInt } = storeToRefs(useUserStore())

  if (!isFromInt.value) {
    useSessionStorage().deleteSessionItem("goto_param")
  }
}

// Loader for Screening datas
const loadScreeningData = fromPromise(async () => {
  const userStore = useUserStore()
  const { fetchScreeningData } = useAnswersStore()

  const { nextAssessment, user } = storeToRefs(userStore)

  const permissions = user.value?.permissions

  if (permissions?.includes("cst_na")) {
    return { isConsentNeeded: true }
  }

  if (!nextAssessment.value) {
    await fetchScreeningData()
    await userStore.fetchAssessmentSummary()
  }

  const assessmentData = nextAssessment.value?.next_assessment

  if (!nextAssessment.value || !assessmentData?.assessment) {
    useSessionStorage().deleteSessionItem("origin")
    useSessionStorage().deleteSessionItem("goto_param")
    throw new Error("NO_NEXT_ASSESSMENT")
  }

  if (assessmentData?.assessment !== "screening") {
    useSessionStorage().deleteSessionItem("origin")
    useSessionStorage().deleteSessionItem("goto_param")
    globalActor.send({
      type: `GLOBAL.REDIRECT.${assessmentData?.assessment.toUpperCase()}` as GlobalMachineEvent,
    })
    return null
  }

  return assessmentData
})

export const screeningMachine = setup({
  actors: {
    loadScreeningData,
  },
  types: {} as {
    events: any
    context: {
      errorSource?: string
    }
  },
  actions: {
    setErrorSource: ({ context }, params: { source: string }) => {
      context.errorSource = params.source
    },
  },
  guards: {
    checkGoToParam: () => getGotoParam() === "screening-authorize", // Has goto if user is coming from auth
    checkNoPmUuid: () => {
      const pm_uuid = useCookies().get(COOKIES.PM_UUID)
      return !pm_uuid
    },
    checkScreeningToken: () => {
      // Has token if the user closed the screening tab
      const token = localStorage.getItem("screening-token")
      return !!token
    },
  },
}).createMachine({
  id: "screeningMachine",
  initial: "checkEntry",
  states: {
    checkEntry: {
      invoke: {
        src: "loadScreeningData",
        onDone: [
          {
            guard: ({ event }) =>
              event.output != null &&
              "isConsentNeeded" in event.output &&
              event.output.isConsentNeeded === true,
            target: "intro",
          },
          {
            target: "checkConditions",
          },
        ],
        onError: {
          actions: () => {
            globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
          },
        },
      },
    },
    checkConditions: {
      always: [
        { target: "noPmUuid", guard: "checkNoPmUuid" },
        // We want to skip intro page if the user has already seen it so redirect to authorize
        { target: "authorize", guard: "checkGoToParam" },
        { target: "authorize", guard: "checkScreeningToken" },
        { target: "intro" },
      ],
    },
    noPmUuid: {
      entry: [
        () => {
          globalActor.send({ type: "GLOBAL.SCREENING.DONE" }) // We cannot access screening if we don't have a pm_uuid
        },
      ],
    },
    intro: {
      entry: [
        () => removeGoToSessionItem(),
        () => router.replace({ name: "screeningIntro" }),
      ],
      on: {
        done: { target: "authorize" },
      },
    },
    authorize: {
      entry: [
        () => removeGoToSessionItem(),
        () => router.replace({ name: "screeningAuthorize" }),
      ],
      on: {
        done: { target: "roomLoading" },
      },
    },
    roomLoading: {
      entry: [
        () => useSessionStorage().deleteSessionItem("origin"), // We can delete screening origin because the user can access the workflow
        () => router.replace({ name: "screeningRoomLoading" }),
      ],
      on: {
        done: { target: "roomTest" },
        error: { target: "screeningError" },
      },
    },
    screeningError: {
      entry: [
        ({ context }) =>
          router.replace({
            name: "screeningError",
            query: context.errorSource
              ? { source: context.errorSource }
              : undefined,
          }),
      ],
    },
    roomTest: {
      entry: [
        () => useSessionStorage().deleteSessionItem("origin"),
        () => router.replace({ name: "screeningTest" }),
      ],
      on: {
        error: { target: "screeningError" },
        done: { target: "screeningReview" },
        loadResults: { target: "screeningLoadResults" },
      },
    },
    screeningReview: {
      entry: [() => router.replace({ name: "screeningReview" })],
      on: {
        done: { target: "screeningLoadResults" },
        error: { target: "screeningError" },
      },
    },
    screeningLoadResults: {
      entry: [() => router.replace({ name: "screeningLoadResults" })],
      on: {
        error: {
          target: "screeningError",
          actions: {
            type: "setErrorSource",
            params: { source: "results" }, // Show screening error page with ?source=results for generic texts
          },
        },
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export const screeningActor = createActor(screeningMachine)

const logoutAndRedirectToAuth = (email: string) => {
  const cookies = useCookies()
  const { clearScreeningStorage, clearScreeningCookies } = useAnswersStore()
  const authUrl = useConfigStore().urls.auth_url

  const encodedEmail = encodeURIComponent(email)
  const registerAuthUrl = `${authUrl}/register/personal?from=screening&email=${encodedEmail}`

  // Set cookie to keep the workflow and call next assessment after register
  cookies.set(COOKIES.GOTO_NEXT, 1, {
    path: "/",
    domain: useConfigStore().urls.cookie_domain,
  })

  // Clear all screening-related cookies
  cookies.remove(COOKIES.PM_UUID)
  cookies.remove(COOKIES.COMPANY_UUID)
  cookies.remove(COOKIES.REFRESH_TOKEN)
  cookies.remove(COOKIES.ACCESS_TOKEN)
  clearScreeningStorage()
  clearScreeningCookies()

  logout()
  window.location.href = registerAuthUrl
}

screeningActor.subscribe({
  async complete() {
    const userStore = useUserStore()
    const cookies = useCookies()
    const { fetchScreeningData } = useAnswersStore()
    const { nextAssessmentName, user } = storeToRefs(userStore)

    await fetchScreeningData()

    const isGuestAcc = user.value.permissions.includes("gu_acc")
    const userEmail = user.value.user.email

    if (!nextAssessmentName.value) {
      cookies.remove(COOKIES.PM_UUID)
      cookies.remove(COOKIES.COMPANY_UUID)
      globalActor.send({ type: "GLOBAL.SCREENING.DONE" })
      return
    }

    if (nextAssessmentName.value === "meeting") {
      // Pending guest user can book meeting
      globalActor.send({
        type: `GLOBAL.REDIRECT.MEETING` as GlobalMachineEvent,
      })
      return
    }

    if (isGuestAcc) {
      // Guest user cannot pass other assessment make him register
      logoutAndRedirectToAuth(userEmail)
    } else {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    }
  },
})
